import { useEffect, useRef, useCallback } from "react";
import { recordCaseTime } from "../services/auth";
import { useIdleTimer } from "./useIdleTimer";
import { ANNOTATION_IDLE_THRESHOLD_MS } from "./useAnnotationActiveTime";

/** Ignore sub-2-second flickers (e.g. accidental clicks) */
const MIN_SESSION_SECONDS = 2;

/**
 * Tracks how long an annotator views a case and flushes the elapsed time
 * to the backend on case change, page unload, tab hide, or inactivity.
 *
 * Time accumulates server-side via atomic ADD — multiple sessions on the
 * same case add up. No time is recorded once the case is marked complete.
 *
 * Idle handling: pauses on tab-hide (visibilitychange) and after
 * ANNOTATION_IDLE_THRESHOLD_MS of no input. On idle the session is flushed up
 * to the *last input*, not "now", so the idle gap itself is never banked; the
 * next input resumes a fresh session.
 */
export function useCaseSessionTimer(
  projectId: string,
  studyInstanceUid: string,
  isCaseComplete: boolean,
  enabled = true,
) {
  const startRef = useRef<number | null>(null);
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  const projectRef = useRef(projectId);
  const studyRef = useRef(studyInstanceUid);
  const completeRef = useRef(isCaseComplete);

  // Keep refs in sync so flush callbacks read current values
  projectRef.current = projectId;
  studyRef.current = studyInstanceUid;
  completeRef.current = isCaseComplete;

  const flush = useCallback(() => {
    if (startRef.current === null) return;
    const elapsed = (Date.now() - startRef.current) / 1000;
    startRef.current = null;
    if (!enabledRef.current || elapsed < MIN_SESSION_SECONDS) return;
    recordCaseTime(projectRef.current, studyRef.current, elapsed);
  }, []);

  // Flush the session as of a given cutoff (used on idle: record up to the
  // last input so the trailing idle gap is excluded).
  const flushUpTo = useCallback((cutoffMs: number) => {
    if (startRef.current === null) return;
    const elapsed = (cutoffMs - startRef.current) / 1000;
    startRef.current = null;
    if (!enabledRef.current || elapsed < MIN_SESSION_SECONDS) return;
    recordCaseTime(projectRef.current, studyRef.current, elapsed);
  }, []);

  const startTimer = useCallback(() => {
    if (enabledRef.current && !completeRef.current) {
      startRef.current = Date.now();
    }
  }, []);

  // Pause on inactivity. On idle, flush up to the last input (excluding the
  // idle gap); the next input fires onActive and resumes a fresh session.
  // msSinceLastInput lets us compute the precise pre-idle cutoff.
  const idleApiRef = useRef<{ msSinceLastInput: () => number } | null>(null);
  const handleIdle = useCallback(() => {
    const msSinceInput = idleApiRef.current?.msSinceLastInput() ?? ANNOTATION_IDLE_THRESHOLD_MS;
    flushUpTo(Date.now() - msSinceInput);
  }, [flushUpTo]);
  const idleApi = useIdleTimer({
    thresholdMs: ANNOTATION_IDLE_THRESHOLD_MS,
    disabled: isCaseComplete || !enabled,
    onIdle: handleIdle,
    onActive: startTimer,
  });
  idleApiRef.current = idleApi;

  // Start/restart timer when case changes; flush previous on cleanup.
  // Capture current IDs in a closure so cleanup flushes to the correct case
  // (refs are already updated to new values by the time React runs cleanup).
  useEffect(() => {
    const capturedProject = projectId;
    const capturedStudy = studyInstanceUid;

    if (!enabled) {
      startRef.current = null;
      return;
    }
    if (isCaseComplete) {
      // Case is done — flush any running timer and don't start a new one
      flush();
      return;
    }
    startTimer();
    return () => {
      if (startRef.current === null) return;
      const elapsed = (Date.now() - startRef.current) / 1000;
      startRef.current = null;
      if (!enabledRef.current || elapsed < MIN_SESSION_SECONDS) return;
      recordCaseTime(capturedProject, capturedStudy, elapsed);
    };
  }, [studyInstanceUid, projectId, isCaseComplete, enabled, flush, startTimer]);

  // Pause on tab hide, resume on tab show
  useEffect(() => {
    const handleVisibility = () => {
      if (document.hidden) {
        flush();
      } else if (!completeRef.current) {
        startTimer();
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, [flush, startTimer]);

  // Flush on page unload (keepalive fetch survives navigation)
  useEffect(() => {
    const handleUnload = () => flush();
    window.addEventListener("beforeunload", handleUnload);
    return () => window.removeEventListener("beforeunload", handleUnload);
  }, [flush]);
}
